import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Text } from '@cotera/client/app/components/ui';
import type { WorkflowDatasetTriggerViewData } from '../types';
import { workflowDatasetViewEntry } from '../../../workflow-artifact-views';
import { useOpenWorkflowResource } from '../../../use-open-workflow-resource';
import { ResourceType } from '@cotera/api';

/**
 * The inspector surface for a dataset trigger.
 *
 * A dataset trigger has no config form of its own — what "edit" means for it is
 * looking at the rows it fires on — so this mirrors the agent / run-workflow
 * node editors: it stays selectable in the canvas (which is what makes it
 * deletable) and offers an explicit "Open dataset" button through to the full
 * dataset page, rather than clicking the card yanking you there. The drawer
 * header already renders the trigger's icon, title and one-line summary.
 */
export const DatasetTriggerEditor: React.FC<{
  workflowId: string;
  trigger: WorkflowDatasetTriggerViewData;
  onOpened?: () => void;
}> = ({ workflowId, trigger, onOpened }) => {
  const openWorkflowResource = useOpenWorkflowResource();

  const openDataset = (): void => {
    // A dataset trigger without a dataset cannot exist — the wizard and the API
    // both require one — so an empty id means the graph is corrupt, and there is
    // nothing to open.
    if (trigger.datasetId === '') {
      return;
    }
    openWorkflowResource({
      resourceType: ResourceType.Dataset,
      artifactId: trigger.datasetId,
      fallbackEntry: workflowDatasetViewEntry({
        workflowId,
        datasetId: trigger.datasetId,
        datasetName: trigger.name,
        fallbackTitle:
          trigger.name.trim() !== '' ? trigger.name : 'Dataset change',
      }),
    });
    onOpened?.();
  };

  return (
    <div className="space-y-3">
      <Text.Caption className="text-muted-foreground block">
        This workflow runs when the dataset changes. Open the dataset to see the
        rows it watches and edit what it fires on.
      </Text.Caption>
      <Button
        type="button"
        variant="outline"
        className="w-full gap-2"
        onClick={openDataset}
      >
        <ExternalLink className="h-4 w-4" />
        Open dataset
      </Button>
    </div>
  );
};
